import React from "react";
import { Link } from "react-router-dom";
import styles from "./styles.module.scss";
import useCommonStore from "../../../stores/common";
import useShippingStore from "../../../stores/shipping";
import useUserStore from "../../../stores/user";
import { getStatusBadgeContent } from "../utils/status";
import { Copier } from "../../../../components/copier";
import CopyIcon from "../../../assets/svg/icons/common/copy";
import LinkIcon from "../../../assets/svg/icons/common/link";
import CloseIcon24 from "../../../assets/svg/icons/common/CloseIcon24";
import ButtonIcon from "../../../components/ButtonIcon/ButtonIcon";
import HeadingWithLine from "../../../components/HeadingWithLine/HeadingWithLine";
import equipmentPlaceholder from "../../../../../images/v2/equipment_placeholder.png";

const Details = () => {
  const showDetails = useCommonStore((state) => state.showDetails);
  const setShowDetails = useCommonStore((state) => state.setShowDetails);
  const shipment = useShippingStore((state) => state.selectedShipping);
  const isEntitySuperAdmin = useUserStore((state) => state.isEntitySuperAdmin);

  if (!showDetails) {
    return null;
  }

  if (!shipment) {
    return (
      <div className={styles.details_container}>
        <div className={styles.close_row}>
          <ButtonIcon onClick={() => setShowDetails(false)}>
            <CloseIcon24 />
          </ButtonIcon>
        </div>
      </div>
    );
  }

  const badge = getStatusBadgeContent(shipment.status);
  const canViewMerchant = isEntitySuperAdmin && shipment.powerapp_id;

  return (
    <div className={styles.details_container}>
      <div className={styles.close_row}>
        <ButtonIcon onClick={() => setShowDetails(false)}>
          <CloseIcon24 />
        </ButtonIcon>
      </div>
      <div className={styles.header}>
        <div className={styles.header_left}>
          <div className={styles.title_section}>
            <h1 className={styles.shipment_title}>
              Shipment: {shipment.shipment_number || "-"}
            </h1>
            <div className={`${styles.status_badge} ${styles[badge.variant]}`}>
              {badge.icon}
              <span>{badge.text}</span>
            </div>
          </div>
        </div>
        {canViewMerchant && (
          <div className={styles.header_right}>
            <Link
              to={`/v2/merchants/${shipment.powerapp_id}`}
              className={styles.to_app_button}
            >
              View Merchant
            </Link>
          </div>
        )}
      </div>

      <div className={styles.content_wrapper}>
        <div className={styles.shipment_info}>
          <div className={styles.info_grid}>
            <div className={styles.info_item}>
              <span className={styles.label}>MID</span>
              <div className={styles.value_with_action}>
                <span>{shipment.mid || "-"}</span>
                {shipment.mid && (
                  <Copier text={shipment.mid}>
                    <button className={styles.copy_button} title="Copy MID">
                      <CopyIcon />
                    </button>
                  </Copier>
                )}
              </div>
            </div>
            <div className={styles.info_item}>
              <span className={styles.label}>Ship Date</span>
              <span className={styles.value}>{shipment.ship_date || "-"}</span>
            </div>
            <div className={styles.info_item}>
              <span className={styles.label}>Ship to</span>
              <span className={styles.value}>{shipment.ship_to || "-"}</span>
            </div>
            <div className={styles.info_item}>
              <span className={styles.label}>Courier</span>
              <span className={styles.value}>{shipment.courier || "-"}</span>
            </div>
            <div className={styles.info_item}>
              <span className={styles.label}>Package TN</span>
              <div className={styles.value_with_action}>
                {shipment.package_tracking_url ? (
                  <a
                    href={shipment.package_tracking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.tracking_link}
                  >
                    {shipment.package_tn || "-"}
                  </a>
                ) : (
                  <span>{shipment.package_tn || "-"}</span>
                )}
                {shipment.package_tracking_url && (
                  <a
                    href={shipment.package_tracking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.link_icon}
                    title="Open tracking"
                  >
                    <LinkIcon />
                  </a>
                )}
              </div>
            </div>
            <div className={styles.info_item}>
              <span className={styles.label}>Call Tag TN</span>
              <div className={styles.value_with_action}>
                <span>{shipment.call_tag_tn || "-"}</span>
                {shipment.call_tag_tn && (
                  <Copier text={shipment.call_tag_tn}>
                    <button className={styles.copy_button} title="Copy call tag tracking number">
                      <CopyIcon />
                    </button>
                  </Copier>
                )}
              </div>
            </div>
          </div>
        </div>

        {shipment.items && shipment.items.length > 0 && (
          <div className={styles.order_section}>
            <HeadingWithLine text="ORDER" margin={false} className={styles.order_section_header} />
            <div className={styles.items_list}>
              {shipment.items.map((item, index) => (
                <div key={item.id} className={styles.order_item}>
                  <div className={styles.item_number}>{index + 1}</div>
                  <div className={`${styles.item_image} ${styles.item_image_placeholder}`}>
                    <img src={equipmentPlaceholder} alt={item.name} />
                  </div>
                  <div className={styles.item_details}>
                    <div className={styles.item_name}>{item.name}</div>
                    <div className={styles.item_sku}>{item.sku}</div>
                  </div>
                  <div className={styles.item_quantity}>X {item.quantity}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={styles.order_details_section}>
          <div className={styles.order_details_grid}>
            <div className={styles.order_detail_item}>
              <span className={styles.label}>Order</span>
              <span className={styles.value}>{shipment.order_number || "-"}</span>
            </div>
            <div className={styles.order_detail_item}>
              <span className={styles.label}>Order date</span>
              <span className={styles.value}>{shipment.order_date || "-"}</span>
            </div>
            <div className={styles.order_detail_item}>
              <span className={styles.label}>Type</span>
              <span className={styles.value}>{shipment.type || "-"}</span>
            </div>
            <div className={styles.order_detail_item}>
              <span className={styles.label}>DBA</span>
              <div className={styles.value_with_link}>
                <span>{shipment.dba || "-"}</span>
              </div>
            </div>
            <div className={styles.order_detail_item}>
              <span className={styles.label}>Office</span>
              <span className={styles.value}>{shipment.office || "-"}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Details;
